import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { SystemStyleObject } from '@mui/system';
import React from 'react';

import InfoIcon from '@/assets/icons/info-icon.svg?react';
import { HIGH_PRECISION_DECIMAL_PLACES } from '@/constants';
import { useLocalizedFormatter } from '@/hooks/useLocalizedFormatter';
import { useTranslator } from '@/hooks/useTranslator';
import { CurrencyCodeType } from '@/types/currency';

interface Props {
  readonly marketExchangeRate?: number | null;
  readonly conversionRate?: number | null;
  readonly integratorRate?: number | null;
  readonly integratorSpreadFeePercentage?: string | null;
  readonly effectiveTransactionValue?: number | null;
  readonly fromCurrency: CurrencyCodeType;
  readonly toCurrency: CurrencyCodeType;
  readonly calizaExchangeSpreadFeePercentage?: string | null;
  readonly fxLockSpreadPercentage?: number | null;
}

const MAJOR_CURRENCIES = [CurrencyCodeType.USD, CurrencyCodeType.USDC, CurrencyCodeType.USDT];

const RATE_FORMATTER_CONFIG = {
  maximumFractionDigits: HIGH_PRECISION_DECIMAL_PLACES,
  minimumFractionDigits: HIGH_PRECISION_DECIMAL_PLACES,
};

const PERCENTAGE_FORMATTER_CONFIG = {
  maximumFractionDigits: 2,
  minimumFractionDigits: 2,
};

interface FormatRateParams {
  rate: number | null | undefined;
  fromCurrency: CurrencyCodeType;
  toCurrency: CurrencyCodeType;
  rateFormatter: Intl.NumberFormat;
}

const formatRate = ({
  rate,
  fromCurrency,
  toCurrency,
  rateFormatter,
}: FormatRateParams): string => {
  if (
    rate === null ||
    rate === undefined ||
    rate === 0 ||
    Number.isNaN(rate) ||
    (MAJOR_CURRENCIES.includes(fromCurrency) && MAJOR_CURRENCIES.includes(toCurrency))
  ) {
    return `1 ${fromCurrency} = 1 ${toCurrency}`;
  }

  if (MAJOR_CURRENCIES.includes(toCurrency)) {
    const invertedRate: number = 1 / rate;
    return `1 ${toCurrency} = ${rateFormatter.format(invertedRate)} ${fromCurrency}`;
  }

  return `1 ${fromCurrency} = ${rateFormatter.format(rate)} ${toCurrency}`;
};

export const ConversionRatesInfo: React.FC<Props> = (props: Props): React.ReactElement | null => {
  const {
    marketExchangeRate,
    conversionRate,
    integratorRate,
    integratorSpreadFeePercentage,
    effectiveTransactionValue,
    fromCurrency,
    toCurrency,
    calizaExchangeSpreadFeePercentage,
    fxLockSpreadPercentage,
  } = props;
  const tr = useTranslator();
  const rateFormatter = useLocalizedFormatter(RATE_FORMATTER_CONFIG);
  const percentageFormatter = useLocalizedFormatter(PERCENTAGE_FORMATTER_CONFIG);

  const midMarketRateFormatted = React.useMemo((): string | null => {
    return formatRate({ rate: marketExchangeRate, fromCurrency, toCurrency, rateFormatter });
  }, [marketExchangeRate, fromCurrency, toCurrency, rateFormatter]);

  const conversionRateFormatted = React.useMemo((): string => {
    return formatRate({ rate: conversionRate, fromCurrency, toCurrency, rateFormatter });
  }, [conversionRate, fromCurrency, rateFormatter, toCurrency]);

  const integratorRateFormatted = React.useMemo((): string | null => {
    if (integratorRate == null) {
      return null;
    }

    return formatRate({ rate: integratorRate, fromCurrency, toCurrency, rateFormatter });
  }, [integratorRate, fromCurrency, toCurrency, rateFormatter]);

  const formattedVET = React.useMemo((): string | null => {
    const isBRLOperation =
      fromCurrency === CurrencyCodeType.BRL || toCurrency === CurrencyCodeType.BRL;

    if (!isBRLOperation) {
      return null;
    }

    return formatRate({ rate: effectiveTransactionValue, fromCurrency, toCurrency, rateFormatter });
  }, [effectiveTransactionValue, fromCurrency, rateFormatter, toCurrency]);

  const tooltipContent = React.useMemo((): React.ReactElement | null => {
    const tooltipItems: string[] = [];

    if (calizaExchangeSpreadFeePercentage) {
      tooltipItems.push(tr('CALIZA_RATE_TOOLTIP', calizaExchangeSpreadFeePercentage));
    }

    if (fxLockSpreadPercentage && fxLockSpreadPercentage > 0) {
      tooltipItems.push(
        tr('FX_LOCK_RATE_TOOLTIP', percentageFormatter.format(fxLockSpreadPercentage)),
      );
    }

    if (integratorSpreadFeePercentage) {
      tooltipItems.push(tr('CUSTOMER_RATE_TOOLTIP', integratorSpreadFeePercentage));
    }

    if (formattedVET) {
      tooltipItems.push(tr('VET_TOOLTIP'));
    }

    if (tooltipItems.length === 0) {
      return null;
    }

    if (tooltipItems.length === 1) {
      return <Typography>{tooltipItems.at(0)}</Typography>;
    }

    return (
      <Stack component="ul" sx={styles.tooltip}>
        {tooltipItems.map(
          (item: string, index: number): React.ReactElement => (
            <Typography key={index} component="li">
              {item}
            </Typography>
          ),
        )}
      </Stack>
    );
  }, [
    calizaExchangeSpreadFeePercentage,
    fxLockSpreadPercentage,
    percentageFormatter,
    integratorSpreadFeePercentage,
    formattedVET,
    tr,
  ]);

  if (!midMarketRateFormatted && !conversionRate && !formattedVET) {
    return null;
  }

  return (
    <Box sx={styles.container}>
      <Stack direction="column" gap={0.5} flex={1}>
        {midMarketRateFormatted && (
          <Typography variant="caption">
            {tr('MID_MARKET_RATE')}: {midMarketRateFormatted} ({tr('MID_MARKET_RATE_SOURCE')})
          </Typography>
        )}
        <Typography variant="caption">
          {tr('CALIZA_RATE')}: {conversionRateFormatted}
        </Typography>
        {integratorRateFormatted && (
          <Typography variant="caption">
            {tr('FX_MARKUP_CUSTOMER_RATE')}: {integratorRateFormatted}
          </Typography>
        )}
        {formattedVET && (
          <Typography variant="caption">
            {tr('VET')}: {formattedVET}
          </Typography>
        )}
      </Stack>
      {tooltipContent && (
        <Tooltip title={tooltipContent} placement="top">
          <Box width={20} height={20}>
            <InfoIcon fill="currentColor" width={20} height={20} />
          </Box>
        </Tooltip>
      )}
    </Box>
  );
};

const styles: Record<string, SystemStyleObject> = {
  container: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 1,
    py: 2,
    px: 2.5,
    borderRadius: 4,
    color: 'custom.LightGray',
    bgcolor: 'custom.Primary1Background',
  },
  tooltip: {
    margin: 0,
    paddingLeft: 2,
    gap: 1,
  },
};
